import { BreakpointObserver } from '@angular/cdk/layout';
import { Location, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { Router, NavigationEnd, ActivatedRoute, Data } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { buildDocumentTitle } from 'src/app/core/branding/public-brand';
import { AdminNavigationHistoryService } from '@core/shell/admin-navigation-history.service';
import {
  canShowAdminBack,
  normalizeAdminPath,
  resolveAdminBackTarget,
} from '@core/shell/admin-page-back.utils';
import { AdminPageBarService } from '../admin-page-bar/admin-page-bar.service';
import {
  AdminPageBarAction,
  AdminPageBarRouteData,
} from '../admin-page-bar/admin-page-bar.models';
import { AdminNavContextService } from '../admin-nav-context.service';

const PAGE_BAR_PHONE_QUERY = '(max-width: 719px)';

@Component({
  selector: 'app-breadcrumb',
  imports: [
    RouterModule,
    NgTemplateOutlet,
    MatCardModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    TranslateModule,
    TablerIconsModule,
  ],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class AppBreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly pageBarService = inject(AdminPageBarService);
  private readonly navigationHistory = inject(AdminNavigationHistoryService);
  private readonly adminNavContext = inject(AdminNavContextService);
  private readonly breakpoints = inject(BreakpointObserver);

  pageInfo: AdminPageBarRouteData = {};
  private lastPageBarPath = '';

  readonly actions = this.pageBarService.actions;
  readonly titleOverride = this.pageBarService.titleOverride;
  readonly subtitleOverride = this.pageBarService.subtitleOverride;
  readonly leadingExtra = this.pageBarService.leadingExtra;
  readonly leadingExtraPlacement = this.pageBarService.leadingExtraPlacement;
  readonly backOverride = this.pageBarService.backOverride;

  /** Alineado con acciones móviles del page bar (&lt;720). */
  private readonly isPhone = toSignal(
    this.breakpoints.observe(PAGE_BAR_PHONE_QUERY).pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  readonly visibleActions = computed(() => {
    const phone = this.isPhone();
    return this.actions().filter((action) => {
      if (action.hidden) {
        return false;
      }
      if (phone && action.desktopOnly) {
        return false;
      }
      if (!phone && action.phoneOnly) {
        return false;
      }
      return true;
    });
  });

  readonly showEndCluster = computed(() => {
    const hasEndLeading =
      !!this.leadingExtra() && this.leadingExtraPlacement() !== 'titleInline';
    return hasEndLeading || this.visibleActions().length > 0;
  });

  /** Desktop: actions that stay as buttons (not `menu: true`). */
  readonly desktopBarActions = computed(() =>
    this.visibleActions().filter((action) => !action.menu),
  );

  /** Desktop overflow for `menu: true` actions. */
  readonly desktopMenuActions = computed(() =>
    this.visibleActions().filter((action) => !!action.menu),
  );

  readonly mobilePrimaryAction = computed(() => {
    const visible = this.visibleActions();
    const barPreferred = visible.filter((action) => !action.menu);
    const pool = barPreferred.length > 0 ? barPreferred : visible;
    return pool.find((action) => action.variant === 'primary') ?? pool[0] ?? null;
  });

  /** Teléfono: primary + acciones con `inlineOnMobile` (sin `menu`). */
  readonly mobileInlineActions = computed(() => {
    const visible = this.visibleActions();
    const primary = this.mobilePrimaryAction();
    if (!primary) {
      return [];
    }
    const inlineIds = new Set<string>([primary.id]);
    for (const action of visible) {
      if (!action.menu && action.inlineOnMobile) {
        inlineIds.add(action.id);
      }
    }
    return visible.filter((action) => inlineIds.has(action.id));
  });

  readonly mobileMenuActions = computed(() => {
    const visible = this.visibleActions();
    const inlineIds = new Set(this.mobileInlineActions().map((action) => action.id));
    return visible.filter((action) => !inlineIds.has(action.id));
  });

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(map(() => this.activatedRoute))
      .pipe(
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(filter((route) => route.outlet === 'primary'))
      .subscribe((leaf) => {
        // Merge ancestor → leaf so nested tabs (p. ej. /reports/ventas/productos)
        // heredan pageBar/title del padre y no vacían la page bar.
        const data = mergeAdminPageBarRouteData(leaf);
        this.titleService.setTitle(buildDocumentTitle(this.resolveTitle(data)));

        const path = normalizeAdminPath(this.router.url);
        const pathChanged = path !== this.lastPageBarPath;
        this.lastPageBarPath = path;

        if (!data.pageBar) {
          this.pageBarService.clear();
        } else if (pathChanged) {
          this.pageBarService.setTitleOverride(null);
          this.pageBarService.setSubtitleOverride(null);
          this.pageBarService.setBackOverride(null);
          this.pageBarService.clearStaleMyPageActions();
        }

        this.pageInfo = data;
      });
  }

  titleText(): string {
    const override = this.pageBarService.titleOverride();
    if (override) {
      return override;
    }
    if (typeof this.pageInfo.titleKey === 'string' && this.pageInfo.titleKey.trim()) {
      return this.translate.instant(this.pageInfo.titleKey);
    }
    return this.pageInfo.title ?? '';
  }

  subtitleText(): string | null {
    const override = this.pageBarService.subtitleOverride();
    if (override) {
      return override;
    }
    if (typeof this.pageInfo.subtitle === 'string' && this.pageInfo.subtitle.trim()) {
      return this.pageInfo.subtitle;
    }
    if (typeof this.pageInfo.subtitleKey === 'string' && this.pageInfo.subtitleKey.trim()) {
      return this.translate.instant(this.pageInfo.subtitleKey);
    }
    return null;
  }

  /**
   * Módulo padre (Ventas, Finanzas…) cuando hay subnav.
   * Solo teléfono: en desktop el rail ya indica el módulo.
   * No duplica si el título de página ya es el nombre del módulo.
   */
  moduleEyebrowText(): string | null {
    if (!this.isPhone() || !this.pageInfo.pageBar || !this.adminNavContext.showEyebrow()) {
      return null;
    }
    const raw = this.adminNavContext.moduleLabel();
    if (!raw) {
      return null;
    }
    const label = this.translate.instant(raw);
    const title = this.titleText().trim();
    if (!label || label === title || raw === title) {
      return null;
    }
    return label;
  }

  private backNavigationInput() {
    return {
      currentUrl: this.router.url,
      routeData: this.pageInfo,
      previousInAppUrl: this.navigationHistory.previousInAppUrl(),
    };
  }

  showBack(): boolean {
    if (this.backOverride()) {
      return true;
    }
    return canShowAdminBack(this.backNavigationInput());
  }

  backAriaLabel(): string {
    const overrideTitle = this.backOverride()?.title?.trim();
    if (overrideTitle) {
      return this.translate.instant('layout.backTo', { name: overrideTitle });
    }
    const parentTitle = this.breadcrumbParentTitle();
    if (parentTitle) {
      return this.translate.instant('layout.backTo', { name: parentTitle });
    }
    return this.translate.instant('layout.back');
  }

  private breadcrumbParentTitle(): string | null {
    const urls = this.pageInfo.urls ?? [];
    if (urls.length <= 1) {
      return null;
    }
    const parent = urls[urls.length - 2];
    const title = parent?.title?.trim();
    return title || null;
  }

  goBack(): void {
    const override = this.backOverride()?.url?.trim();
    if (override) {
      void this.router.navigateByUrl(override);
      return;
    }
    const target = resolveAdminBackTarget(this.backNavigationInput());
    if (target) {
      void this.router.navigateByUrl(target);
      return;
    }
    this.location.back();
  }

  actionLabel(action: AdminPageBarAction): string {
    if (action.label) {
      return action.label;
    }
    if (action.labelKey) {
      return this.translate.instant(action.labelKey);
    }
    return '';
  }

  /** Label visible en botones inline del teléfono (corta si existe). */
  mobileActionLabel(action: AdminPageBarAction): string {
    if (action.shortLabel?.trim()) {
      return action.shortLabel.trim();
    }
    if (action.shortLabelKey?.trim()) {
      return this.translate.instant(action.shortLabelKey);
    }
    return this.actionLabel(action);
  }

  visibleMenuItems(action: AdminPageBarAction): readonly AdminPageBarAction[] {
    return (action.menuItems ?? []).filter((item) => !item.hidden);
  }

  /** Iconify ids llevan `prefix:name` (p. ej. `tabler:cash-register`). */
  isIconifyActionIcon(icon: string | undefined): boolean {
    return !!icon && icon.includes(':');
  }

  actionButtonClass(action: AdminPageBarAction): string {
    switch (action.variant) {
      case 'secondary':
        return 'milinco-btn-secondary';
      case 'outline':
        return 'milinco-btn-outline';
      case 'primary':
      default:
        return 'milinco-btn-primary';
    }
  }

  runAction(action: AdminPageBarAction): void {
    if (action.disabled || action.hidden) {
      return;
    }
    action.handler();
  }

  private resolveTitle(data: AdminPageBarRouteData): string | null {
    const override = this.pageBarService.titleOverride();
    if (override) {
      return override;
    }
    if (typeof data.titleKey === 'string' && data.titleKey.trim()) {
      return this.translate.instant(data.titleKey);
    }
    return typeof data.title === 'string' ? data.title : null;
  }
}

/** Ancestor → leaf: el hijo puede omitir pageBar/title y heredar del padre. */
function mergeAdminPageBarRouteData(leaf: ActivatedRoute): AdminPageBarRouteData {
  const chain: ActivatedRoute[] = [];
  let route: ActivatedRoute | null = leaf;
  while (route) {
    chain.push(route);
    route = route.parent;
  }
  const merged: Record<string, unknown> = {};
  for (const node of chain.reverse()) {
    Object.assign(merged, node.snapshot.data as Data);
  }
  return merged as AdminPageBarRouteData;
}
